import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, RefreshCw, Upload, Download } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { throwEdgeFunctionError } from '@/lib/edgeFunctionError';
import type { Database } from '@/types/database';

type AgentRow = Database['public']['Tables']['agents']['Row'];
type GuardianRow = Database['public']['Tables']['guardians']['Row'];
type AgentFormData = Omit<AgentRow, 'created_at' | 'updated_at'>;
type AddMode = 'existing' | 'create';

interface ElevenLabsAgentSummary {
  agentId: string;
  name: string;
}

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
}> = ({ label, value, onChange, multiline }) => (
  <div>
    <label className="block text-xs font-medium text-[#555555] mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] resize-none"
      />
    ) : (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A]"
      />
    )}
  </div>
);

const defaultAgent = (): AgentFormData => ({
  id: '',
  name: '',
  description: '',
  personality: '',
  scenario: '',
  system_prompt: '',
  first_message: '',
  voice_id: '',
  guardian_id: null,
});

const AgentForm: React.FC<{
  initial: AgentFormData;
  guardians: GuardianRow[];
  onSave: (data: AgentFormData) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  saveLabel?: string;
  idFieldRenderer?: (args: {
    form: AgentFormData;
    setField: <K extends keyof AgentFormData>(key: K, value: AgentFormData[K]) => void;
  }) => React.ReactNode;
}> = ({ initial, guardians, onSave, onCancel, saving, saveLabel = 'Save', idFieldRenderer }) => {
  const [form, setForm] = useState(initial);
  const setField = <K extends keyof AgentFormData>(key: K, value: AgentFormData[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <div className="border border-[#DADADA] rounded-xl p-4 bg-[#FAFAFA] space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {idFieldRenderer ? (
          idFieldRenderer({ form, setField })
        ) : (
          <Field label="Agent ID (ElevenLabs agent_id)" value={form.id} onChange={(value) => setField('id', value)} />
        )}
        <Field label="Name" value={form.name} onChange={(value) => setField('name', value)} />
      </div>

      <Field label="Description" value={form.description} onChange={(value) => setField('description', value)} multiline />
      <Field label="Personality" value={form.personality} onChange={(value) => setField('personality', value)} multiline />
      <Field label="Scenario" value={form.scenario} onChange={(value) => setField('scenario', value)} multiline />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#555555] mb-1">Guardian</label>
          <select
            value={form.guardian_id ?? ''}
            onChange={(e) => setField('guardian_id', e.target.value || null)}
            className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] bg-white"
          >
            <option value="">None</option>
            {guardians.map((guardian) => (
              <option key={guardian.id} value={guardian.id}>
                {guardian.name} ({guardian.id})
              </option>
            ))}
          </select>
        </div>
        <Field label="Voice ID" value={form.voice_id} onChange={(value) => setField('voice_id', value)} />
      </div>

      <Field label="System Prompt" value={form.system_prompt} onChange={(value) => setField('system_prompt', value)} multiline />
      <Field label="First Message" value={form.first_message} onChange={(value) => setField('first_message', value)} multiline />

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => void onSave(form)}
          disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333] disabled:opacity-50"
        >
          <Check size={14} />
          {saving ? 'Saving…' : saveLabel}
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 border border-[#DADADA] text-sm rounded-lg hover:bg-[#F5F5F5] text-[#555555]"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </div>
  );
};

const AgentsAdmin: React.FC = () => {
  const [rows, setRows] = useState<AgentRow[]>([]);
  const [guardians, setGuardians] = useState<GuardianRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>('existing');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [loadingAvailableAgents, setLoadingAvailableAgents] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<ElevenLabsAgentSummary[]>([]);
  const [remoteConfigById, setRemoteConfigById] = useState<
    Record<string, { voiceId: string; firstMessage: string; systemPrompt: string }>
  >({});

  const fetchData = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError(null);

    const [agentsResult, guardiansResult] = await Promise.all([
      supabase.from('agents').select('*').order('name'),
      supabase.from('guardians').select('*').order('name'),
    ]);

    const firstError = agentsResult.error ?? guardiansResult.error;
    if (firstError) {
      setError(firstError.message);
    } else {
      setRows(agentsResult.data ?? []);
      setGuardians(guardiansResult.data ?? []);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const invokeManageElevenLabsAgent = useCallback(
    async <T,>(body: Record<string, unknown>): Promise<T> => {
      if (!supabase) {
        throw new Error('Supabase client is not initialized.');
      }
      const { data: authData } = await supabase.auth.getSession();
      const accessToken = authData.session?.access_token;
      if (!accessToken) {
        throw new Error('You must be signed in as an admin.');
      }

      const { data, error: functionError } = await supabase.functions.invoke<T & { error?: string; details?: unknown }>(
        'manage-elevenlabs-agent',
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          body,
        },
      );

      if (functionError) {
        await throwEdgeFunctionError(functionError);
      }

      if (data && typeof data === 'object' && 'error' in data && data.error) {
        throw new Error(`${String(data.error)}${data.details ? `: ${JSON.stringify(data.details)}` : ''}`);
      }

      return data as T;
    },
    [],
  );

  const fetchAvailableElevenLabsAgents = useCallback(async () => {
    setLoadingAvailableAgents(true);
    setError(null);
    try {
      const response = await invokeManageElevenLabsAgent<{ agents?: unknown }>({ action: 'list' });
      const payloadObj = response.agents && typeof response.agents === 'object' ? (response.agents as Record<string, unknown>) : {};
      const rawAgents = Array.isArray(payloadObj.agents) ? payloadObj.agents : [];
      const mapped = rawAgents
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const record = item as Record<string, unknown>;
          const agentId = typeof record.agent_id === 'string' ? record.agent_id : null;
          const name = typeof record.name === 'string' ? record.name : null;
          if (!agentId || !name) return null;
          return { agentId, name };
        })
        .filter((value): value is ElevenLabsAgentSummary => value !== null);
      setAvailableAgents(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load ElevenLabs agents');
    } finally {
      setLoadingAvailableAgents(false);
    }
  }, [invokeManageElevenLabsAgent]);

  const saveNew = async (form: AgentFormData) => {
    if (!supabase) return;
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase.from('agents').insert(form);
    if (insertError) {
      setError(insertError.message);
      setSaving(false);
      return;
    }
    setAdding(false);
    setSaving(false);
    void fetchData();
  };

  const saveNewCreatedInElevenLabs = async (form: AgentFormData) => {
    if (!supabase) return;
    setSaving(true);
    setError(null);
    try {
      const createResult = await invokeManageElevenLabsAgent<{ created?: unknown }>({
        action: 'create',
        config: {
          name: form.name,
          systemPrompt: form.system_prompt,
          firstMessage: form.first_message,
          voiceId: form.voice_id,
        },
      });
      const createdObj =
        createResult.created && typeof createResult.created === 'object'
          ? (createResult.created as Record<string, unknown>)
          : null;
      const createdAgentId = createdObj && typeof createdObj.agent_id === 'string' ? createdObj.agent_id : null;
      if (!createdAgentId) {
        throw new Error('ElevenLabs did not return a new agent_id.');
      }

      const rowToInsert: AgentFormData = {
        ...form,
        id: createdAgentId,
      };
      const { error: insertError } = await supabase.from('agents').insert(rowToInsert);
      if (insertError) {
        throw new Error(insertError.message);
      }

      setAdding(false);
      await fetchData();
      await fetchAvailableElevenLabsAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create ElevenLabs agent');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (form: AgentFormData) => {
    if (!supabase) return;
    setSaving(true);
    const { error: updateError } = await supabase.from('agents').update(form).eq('id', form.id);
    if (updateError) {
      setError(updateError.message);
      setSaving(false);
      return;
    }
    setEditing(null);
    setSaving(false);
    void fetchData();
  };

  const remove = async (id: string) => {
    if (!supabase || !confirm('Delete this agent? Kips linked to this agent will lose voice mapping.')) return;
    const { error: deleteError } = await supabase.from('agents').delete().eq('id', id);
    if (deleteError) setError(deleteError.message);
    else void fetchData();
  };

  const invokeAgentSync = async (action: 'fetch' | 'push', agent: AgentRow) => {
    if (!supabase) return;
    setSyncing(`${action}:${agent.id}`);
    setError(null);
    try {
      const data = await invokeManageElevenLabsAgent<{
        agent?: unknown;
      }>(
        action === 'push'
          ? {
              action,
              agentId: agent.id,
              config: {
                name: agent.name,
                systemPrompt: agent.system_prompt,
                firstMessage: agent.first_message,
                voiceId: agent.voice_id,
              },
            }
          : {
              action,
              agentId: agent.id,
            },
      );

      if (action === 'fetch') {
        const agentPayload = data && typeof data === 'object' && 'agent' in data ? data.agent : null;
        const payloadObj = agentPayload && typeof agentPayload === 'object' ? (agentPayload as Record<string, unknown>) : {};
        const conversationConfig = payloadObj.conversation_config as
          | { agent?: { prompt?: { prompt?: string }; first_message?: string }; tts?: { voice_id?: string } }
          | undefined;

        setRemoteConfigById((prev) => ({
          ...prev,
          [agent.id]: {
            voiceId: conversationConfig?.tts?.voice_id ?? '',
            firstMessage: conversationConfig?.agent?.first_message ?? '',
            systemPrompt: conversationConfig?.agent?.prompt?.prompt ?? '',
          },
        }));
      } else {
        alert('Pushed configuration to ElevenLabs.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sync with ElevenLabs');
    } finally {
      setSyncing(null);
    }
  };

  const handleAddAgentSubmit = async (form: AgentFormData) => {
    if (!form.id.trim() && addMode === 'existing') {
      setError('Please select an ElevenLabs agent to add.');
      return;
    }

    if (addMode === 'create') {
      await saveNewCreatedInElevenLabs(form);
      return;
    }

    await saveNew(form);
  };

  const addedAgentIds = new Set(rows.map((row) => row.id));
  const unlinkedAvailableAgents = availableAgents.filter((agent) => !addedAgentIds.has(agent.agentId));

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-5 h-5 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      <div className="flex justify-between items-center">
        <span className="text-sm text-[#555555]">{rows.length} agents</span>
        <div className="flex gap-2">
          <button
            onClick={() => void fetchData()}
            className="flex items-center gap-1 text-sm text-[#555555] hover:text-[#1A1A1A]"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            onClick={() => {
              setAdding(true);
              setAddMode('existing');
              void fetchAvailableElevenLabsAgents();
            }}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333]"
          >
            <Plus size={14} /> Add Agent
          </button>
        </div>
      </div>

      {adding && (
        <div className="space-y-3 border border-[#DADADA] rounded-xl p-4 bg-[#FAFAFA]">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-[#555555]">Choose how to add an agent:</div>
            <div className="inline-flex rounded-lg border border-[#DADADA] overflow-hidden text-sm">
              <button
                onClick={() => {
                  setAddMode('existing');
                  if (availableAgents.length === 0) {
                    void fetchAvailableElevenLabsAgents();
                  }
                }}
                className={`px-3 py-1.5 ${
                  addMode === 'existing' ? 'bg-[#1A1A1A] text-white' : 'bg-white text-[#555555] hover:bg-[#F5F5F5]'
                }`}
              >
                Add Existing
              </button>
              <button
                onClick={() => setAddMode('create')}
                className={`px-3 py-1.5 border-l border-[#DADADA] ${
                  addMode === 'create' ? 'bg-[#1A1A1A] text-white' : 'bg-white text-[#555555] hover:bg-[#F5F5F5]'
                }`}
              >
                Create New
              </button>
            </div>
          </div>

          {addMode === 'existing' && (
            <div className="text-xs text-[#666666]">
              Select an existing ElevenLabs agent from your workspace, then save it to this platform.
            </div>
          )}
          {addMode === 'create' && (
            <div className="text-xs text-[#666666]">
              This will create a new ElevenLabs agent first, then add it here with the returned `agent_id`.
            </div>
          )}

          <AgentForm
            key={`add-form-${addMode}`}
            initial={defaultAgent()}
            guardians={guardians}
            onSave={handleAddAgentSubmit}
            onCancel={() => setAdding(false)}
            saving={saving}
            saveLabel={addMode === 'create' ? 'Create & Save' : 'Save'}
            idFieldRenderer={
              addMode === 'existing'
                ? ({ form, setField }) => (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-[#555555]">ElevenLabs Agent</label>
                        <button
                          type="button"
                          onClick={() => void fetchAvailableElevenLabsAgents()}
                          className="flex items-center gap-1 text-xs text-[#555555] hover:text-[#1A1A1A]"
                          disabled={loadingAvailableAgents}
                        >
                          <RefreshCw size={11} />
                          {loadingAvailableAgents ? 'Loading…' : 'Refresh'}
                        </button>
                      </div>
                      <select
                        value={form.id}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          setField('id', selectedId);
                          const selectedAgent = unlinkedAvailableAgents.find((agent) => agent.agentId === selectedId);
                          if (selectedAgent) {
                            setField('name', selectedAgent.name);
                          }
                        }}
                        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] bg-white"
                        disabled={loadingAvailableAgents}
                      >
                        <option value="">
                          {loadingAvailableAgents
                            ? 'Loading ElevenLabs agents...'
                            : unlinkedAvailableAgents.length > 0
                              ? 'Select an agent'
                              : 'No unlinked agents found'}
                        </option>
                        {unlinkedAvailableAgents.map((agent) => (
                          <option key={agent.agentId} value={agent.agentId}>
                            {agent.name} ({agent.agentId})
                          </option>
                        ))}
                      </select>
                    </div>
                  )
                : ({ form }) => (
                    <div>
                      <label className="block text-xs font-medium text-[#555555] mb-1">Agent ID (generated by ElevenLabs)</label>
                      <input
                        value={form.id ? form.id : 'Will be generated when created'}
                        disabled
                        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm bg-[#F5F5F5] text-[#777777]"
                      />
                    </div>
                  )
            }
          />
        </div>
      )}

      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.id} className="border border-[#DADADA] rounded-xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="font-medium text-[#1A1A1A]">{row.name}</span>
                <span className="ml-2 text-xs text-[#888888]">{row.id}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => void invokeAgentSync('fetch', row)}
                  disabled={syncing !== null}
                  className="flex items-center gap-1 px-2 py-1 rounded border border-[#DADADA] text-xs text-[#555555] hover:bg-[#F5F5F5] disabled:opacity-50"
                >
                  <Download size={12} />
                  Fetch
                </button>
                <button
                  onClick={() => void invokeAgentSync('push', row)}
                  disabled={syncing !== null}
                  className="flex items-center gap-1 px-2 py-1 rounded border border-[#DADADA] text-xs text-[#555555] hover:bg-[#F5F5F5] disabled:opacity-50"
                >
                  <Upload size={12} />
                  Push
                </button>
                <button onClick={() => setEditing(editing === row.id ? null : row.id)} className="text-[#888888] hover:text-[#1A1A1A]">
                  <Pencil size={15} />
                </button>
                <button onClick={() => void remove(row.id)} className="text-[#888888] hover:text-red-500">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>

            {editing === row.id && (
              <div className="border-t border-[#DADADA] p-4">
                <AgentForm
                  initial={{
                    id: row.id,
                    name: row.name,
                    description: row.description,
                    personality: row.personality,
                    scenario: row.scenario,
                    system_prompt: row.system_prompt,
                    first_message: row.first_message,
                    voice_id: row.voice_id,
                    guardian_id: row.guardian_id,
                  }}
                  guardians={guardians}
                  onSave={saveEdit}
                  onCancel={() => setEditing(null)}
                  saving={saving}
                />
              </div>
            )}
            {remoteConfigById[row.id] && (
              <div className="border-t border-[#DADADA] px-4 py-3 bg-[#FAFAFA] text-xs text-[#555555] space-y-1">
                <div className="font-medium text-[#1A1A1A]">Live ElevenLabs config (fetched)</div>
                <div>
                  <span className="font-medium text-[#1A1A1A]">Voice ID:</span> {remoteConfigById[row.id].voiceId || 'Not set'}
                </div>
                <div>
                  <span className="font-medium text-[#1A1A1A]">First Message:</span>{' '}
                  {remoteConfigById[row.id].firstMessage || 'Not set'}
                </div>
                <div>
                  <span className="font-medium text-[#1A1A1A]">System Prompt:</span>{' '}
                  {remoteConfigById[row.id].systemPrompt || 'Not set'}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgentsAdmin;
